// THE BALLOT BUILDER'S PURE CORE — the operator's working draft, the one ratified
// derivation, and the assembly of a wire `CreateBallotSpec`. No DOM, no React: the
// component in `BallotBuilder.tsx` is a thin form over these answers, exactly as
// `state.ts` is the pure core the open/abandon panels are a form over.
//
// ── WHY THIS IS A MODULE AND NOT INLINE IN THE COMPONENT ───────────────────
// This package has NO DOM test environment (README), so a rule that lives inside a
// React callback is held by reading the code and nothing else. The two rules most
// worth a check that can fail — whether the spec about to be POSTed is well formed,
// and what fraction a motion's majority BASE derives — live here, pure over their
// arguments, and `spec.test.ts` drills each in both directions. The form is then a
// thin surface over `validateDraft`, `thresholdForBase` and `buildSpec`.
//
// ── IT IMPORTS ONLY TYPES ──────────────────────────────────────────────────
// Every import is `import type`, fully erased at runtime, so this module has no
// runtime dependency on `../api/client` (which pulls `@cca/ui`) and the test is
// hermetic in the default node environment — the same property `state.ts` has. The
// wire shapes come from `../api/client`, whose types mirror the worker's
// `src/ballot-create.ts`; the three closed unions come from the count engine.

import type { Electorate, MajorityBase } from '@cca/decides-count';
import type { BallotCandidateInput, BallotItemInput, CreateBallotSpec } from '../api/client';

// ═══════════════════════════════════════════════════════════════════════════
//  THE OPERATOR'S WORKING DRAFT — what the form holds while it is being built.
//
//  The ballot is HOMOGENEOUS (its `kind` is ballot-level and the worker refuses a
//  mixed ballot with `item_kind_mismatch`), so the draft carries one `kind` and a
//  list of item drafts all of that kind. Each item draft carries its own `kind`
//  discriminant too, so `buildSpec` can narrow it — the form only ever appends an
//  item of the ballot's current kind, and clears the list when the kind changes.
// ═══════════════════════════════════════════════════════════════════════════

export type BallotKind = 'position' | 'motion';
export type HouseRule = 'one-tally' | 'separate-chambers';
export type Secrecy = 'secret' | 'attributable';

/** One nominee row on a position item, as the operator types it. `key` is a
 *  client-only stable identifier for React reconciliation and DOM ids; it is NEVER
 *  sent — `buildSpec` mints a fresh server id for each candidate. */
export interface CandidateDraft {
  key: string;
  displayName: string;
}

/**
 * A POSITION item being drafted. `electorate` starts `null` — who may vote decides
 * who governs (SO 15.8), so it is a conscious choice with no default, the same
 * treatment `majorityBase` gets on a motion. `seats`/`alternateSeats` are numbers so
 * `buildSpec` passes them straight to the wire; an emptied `seats` becomes `NaN` and
 * is refused by {@link validateDraft} rather than coerced to a silent 1.
 */
export interface PositionItemDraft {
  key: string;
  kind: 'position';
  title: string;
  electorate: Electorate | null;
  seats: number;
  alternateSeats: number;
  method: 'fptp' | 'approval';
  candidates: CandidateDraft[];
}

/**
 * A MOTION item being drafted. `majorityBase` starts `null` (a conscious choice —
 * the wire fraction is DERIVED from it, never typed); `motionClass` is a recorded
 * label and may stay `null` ("Not stated").
 *
 * ── UNSET IS `''` FOR WHAT IS TYPED, `null` FOR WHAT IS CHOSEN ──────────────
 * `instrument`, `mover` and `seconder` are TYPED, so their unset form is the empty
 * string — the same treatment `title` and `CandidateDraft.displayName` get. The two
 * `null`s above are CHOICES with no default, which is a different absence: a control
 * with nothing selected, not a field with nothing in it.
 *
 * `instrument` is the operative words themselves — the substance a member reads and
 * consents to on the paper — and it is REQUIRED: {@link validateDraft} refuses a
 * motion without one, mirroring the worker's `instrument_required` and the database
 * trigger behind it. `mover` and `seconder` are recorded WHERE THEY ARE KNOWN and
 * block nothing: no ruling makes them part of what Synod votes on, and a form that
 * refused a motion for want of a seconder would refuse a lawful motion.
 */
export interface MotionItemDraft {
  key: string;
  kind: 'motion';
  title: string;
  instrument: string;
  mover: string;
  seconder: string;
  majorityBase: MajorityBase | null;
  motionClass: string | null;
}

export type ItemDraft = PositionItemDraft | MotionItemDraft;

/**
 * The whole working draft. `secrecy` is meaningful only for a MOTION ballot — a
 * position is always written 'secret' (SO 15.9), so {@link buildSpec} OMITS secrecy
 * from a position spec rather than sending a value the server would ignore.
 */
export interface BallotDraft {
  title: string;
  kind: BallotKind;
  houseRule: HouseRule;
  secrecy: Secrecy;
  items: ItemDraft[];
}

/** A fresh client-only key. Separate from the server id `buildSpec` mints, so the
 *  form's React keys and DOM ids are stable across an add or a remove without ever
 *  leaking a presentational id onto the wire. */
function freshKey(): string {
  return crypto.randomUUID();
}

export function newCandidate(): CandidateDraft {
  return { key: freshKey(), displayName: '' };
}

/** A new POSITION item — one empty nominee row (SO 15.6 needs at least one), first
 *  past the post, one place, no alternates, and no electorate chosen yet. */
export function newPositionItem(): PositionItemDraft {
  return {
    key: freshKey(),
    kind: 'position',
    title: '',
    electorate: null,
    seats: 1,
    alternateSeats: 0,
    method: 'fptp',
    candidates: [newCandidate()],
  };
}

/** A new MOTION item — nothing typed yet, no base chosen, class not stated. */
export function newMotionItem(): MotionItemDraft {
  return {
    key: freshKey(),
    kind: 'motion',
    title: '',
    instrument: '',
    mover: '',
    seconder: '',
    majorityBase: null,
    motionClass: null,
  };
}

/** A brand-new draft: an election ballot, one tally, secret, and NO items yet — so
 *  the kind selector is live until the first item is added (Miranda: the kind locks
 *  "once the first item exists"). */
export function initialBallotDraft(): BallotDraft {
  return { title: '', kind: 'position', houseRule: 'one-tally', secrecy: 'secret', items: [] };
}

// ═══════════════════════════════════════════════════════════════════════════
//  THE ONE RATIFIED DERIVATION — a motion's majority BASE to its wire fraction.
//
//  Source: docs/decides/BUILDER-THRESHOLD-DERIVATION.md (ratified by the registrar,
//  Steve Jukes, 2026-08-29). The operator picks the base — the SENTENCE a member
//  reads (`thresholds.ts` THRESHOLD_COPY) — and the builder derives the fraction the
//  count enforces, so the two can never drift: there is only one input. A raw
//  numerator/denominator is NEVER exposed.
//
//  Keyed on the engine's own `MajorityBase` union, so a fourth base added upstream
//  reds THIS file rather than shipping a motion with no derived fraction — the same
//  fail-closed keying THRESHOLD_COPY uses for the wording.
// ═══════════════════════════════════════════════════════════════════════════

/** The wire fraction a base derives — the three motion threshold columns
 *  (`thresholdKind` + the integer numerator/denominator), spelt the worker's way. */
export interface ThresholdWire {
  thresholdKind: 'simple' | 'super';
  thresholdNumerator: number | null;
  thresholdDenominator: number | null;
}

const THRESHOLD_BY_BASE: Record<MajorityBase, ThresholdWire> = {
  // A simple majority of those who vote — no fraction columns (the schema refuses a
  // fraction on a 'simple' threshold).
  'present-and-voting': {
    thresholdKind: 'simple',
    thresholdNumerator: null,
    thresholdDenominator: null,
  },
  // Two-thirds of the members summoned to each House (Constitution §15.2, §18.1).
  'summoned-per-house': { thresholdKind: 'super', thresholdNumerator: 2, thresholdDenominator: 3 },
  // Three-quarters of every member of Synod (Constitution §9.7, §19.1).
  'all-synod-members': { thresholdKind: 'super', thresholdNumerator: 3, thresholdDenominator: 4 },
};

/** The ratified fraction for a majority base. Total over `MajorityBase`, so the
 *  compiler — not a test — catches a base with no derivation. */
export function thresholdForBase(base: MajorityBase): ThresholdWire {
  return THRESHOLD_BY_BASE[base];
}

// ═══════════════════════════════════════════════════════════════════════════
//  VALIDATION — the client-side mirror of the worker's `validateSpec`, so Create is
//  refused BEFORE a 422 and the operator is told which field blocks it and why.
//
//  It returns a flat list of ADDRESSED problems (empty = ready to create). The form
//  renders each beside its field (via `aria-describedby`) and lists them as the
//  Create button's disabled-reason, in words. Each `message` is plain NZ-English.
// ═══════════════════════════════════════════════════════════════════════════

/** One reason the draft is not yet ready to create, addressed so the form can point
 *  at the exact field. `index` is the item's position in the list; `candidateIndex`
 *  the nominee row within a position item. The BALLOT-level addresses (`title`,
 *  `items`, `house-rule`) carry no index — there is one of each per draft. */
export type DraftProblem =
  | { at: 'title'; message: string }
  | { at: 'items'; message: string }
  | { at: 'house-rule'; message: string }
  | { at: 'item-title'; index: number; message: string }
  | { at: 'electorate'; index: number; message: string }
  | { at: 'seats'; index: number; message: string }
  | { at: 'alternate-seats'; index: number; message: string }
  | { at: 'candidates'; index: number; message: string }
  | { at: 'candidate-name'; index: number; candidateIndex: number; message: string }
  | { at: 'instrument'; index: number; message: string }
  | { at: 'majority-base'; index: number; message: string };

/**
 * EVERY refusal the worker's `validateSpec` makes that this form can reach,
 * mirrored client-side. It returns ALL problems (not the first), because a form
 * shows every blocked field at once rather than one per round-trip. The refusals it
 * does NOT mirror are the ones this form makes UNREACHABLE by construction — it
 * mints ids and agenda positions (so `item_id_*` / `agenda_position_*` cannot
 * arise), never mixes a motion's and a position's columns, and sets a position's
 * secrecy server-side — and those still have copy in `copy.ts` for the defence in
 * depth of a surprise from the wire.
 */
export function validateDraft(draft: BallotDraft): DraftProblem[] {
  const problems: DraftProblem[] = [];

  if (draft.title.trim() === '') {
    problems.push({ at: 'title', message: 'Give the ballot a title.' });
  }

  // house_rule_required_for_base — the count makes by-House aggregation MANDATORY for
  // that base and takes the flag from the BALLOT's house rule, so the pairing is the one
  // refusal joining a ballot-level field to an item-level one. Addressed to `house-rule`
  // because that is the control to change: the motion's base is the ratified choice, not
  // the fault. `initialBallotDraft` starts 'one-tally', so an amendment meets it by
  // DEFAULT — and unrefused here it is a fatal `by-houses-required` in the meeting.
  // Checked before the item loop so the blocker list reads in the order of the form,
  // where the counting rule sits above the items.
  const hasSummonedPerHouse = draft.items.some(
    (item) => item.kind === 'motion' && item.majorityBase === 'summoned-per-house',
  );
  if (draft.houseRule !== 'separate-chambers' && hasSummonedPerHouse) {
    problems.push({
      at: 'house-rule',
      message:
        'Set how the votes are counted to Separate chambers — a motion measured against the members summoned, per House is counted House by House.',
    });
  }

  // no_items — a ballot needs at least one item.
  if (draft.items.length === 0) {
    const noun = draft.kind === 'motion' ? 'motion' : 'election';
    problems.push({ at: 'items', message: `Add at least one ${noun} to the ballot.` });
  }

  draft.items.forEach((item, index) => {
    // item_title_required.
    if (item.title.trim() === '') {
      problems.push({ at: 'item-title', index, message: 'Give this item a title.' });
    }

    if (item.kind === 'position') {
      // electorate_unknown — no default; who may vote is a conscious choice.
      if (item.electorate === null) {
        problems.push({
          at: 'electorate',
          index,
          message: 'Choose who may vote in this election — the Clergy, the Laity, or both.',
        });
      }
      // seats_invalid — at least one place (an emptied field is NaN, caught here).
      if (!Number.isSafeInteger(item.seats) || item.seats < 1) {
        problems.push({
          at: 'seats',
          index,
          message: 'This election must fill at least one place.',
        });
      }
      // alternate_seats_invalid — nought or more.
      if (!Number.isSafeInteger(item.alternateSeats) || item.alternateSeats < 0) {
        problems.push({
          at: 'alternate-seats',
          index,
          message: 'Alternate places cannot be fewer than nought.',
        });
      }
      // position_needs_candidate (SO 15.6) — a defence-in-depth guard; the form keeps
      // at least one nominee ROW, so this fires only if every row has been removed.
      if (item.candidates.length === 0) {
        problems.push({
          at: 'candidates',
          index,
          message: 'Every election needs at least one nominee (Standing Order 15.6).',
        });
      }
      item.candidates.forEach((candidate, candidateIndex) => {
        const name = candidate.displayName.trim();
        // candidate_display_name_required.
        if (name === '') {
          problems.push({
            at: 'candidate-name',
            index,
            candidateIndex,
            message: 'Enter the nominee’s name, or remove this row.',
          });
        } else if (name.toLowerCase() === 'no confidence') {
          // no_confidence_candidate — mirrors the worker EXACTLY
          // (`displayName.trim().toLowerCase() === 'no confidence'`). No Confidence is
          // a mark choice on every election (SO 15.7), never a nominee row.
          problems.push({
            at: 'candidate-name',
            index,
            candidateIndex,
            message:
              'No Confidence is a choice on every election already, not a nominee — it cannot be entered as a person. Remove this row.',
          });
        }
      });
    } else {
      // instrument_required — the operative words ARE the motion: they are what a
      // member reads on the paper and consents to, so a motion without them is not a
      // motion. Mirrors the worker's refusal, which a database trigger holds behind.
      // No length ceiling in either direction — a constitutional amendment runs to
      // paragraphs, and refusing a lawful motion for being wordy is the over-strict
      // failure Composition of Synod cl 9(1)-(2) forbids.
      if (item.instrument.trim() === '') {
        problems.push({
          at: 'instrument',
          index,
          message: 'Enter the words this motion asks Synod to agree.',
        });
      }
      // `mover`/`seconder` are DELIBERATELY unchecked. They are recorded where they
      // are known; no ruling makes either part of what Synod votes on, and a form
      // that refused a motion for want of a seconder would refuse a lawful motion.

      // majority_base_unknown — the base must be chosen; the wire fraction derives.
      if (item.majorityBase === null) {
        problems.push({
          at: 'majority-base',
          index,
          message: 'Choose what this motion’s majority is measured against.',
        });
      }
    }
  });

  return problems;
}

/** Whether the draft is ready to create — the gate on the Create button, so the
 *  malformed cases never reach the server's 422. */
export function isDraftValid(draft: BallotDraft): boolean {
  return validateDraft(draft).length === 0;
}

// ═══════════════════════════════════════════════════════════════════════════
//  ASSEMBLY — the working draft to a wire `CreateBallotSpec`.
//
//  · Item and candidate ids are minted here with `crypto.randomUUID()` (the server
//    mints the BALLOT id; item and candidate ids are the caller's, because a
//    candidate names its item by id and both ids are inside the definition digest).
//  · `agendaPosition` is derived from LIST ORDER (1-based, contiguous) — never typed,
//    so `agenda_position_invalid`/`_duplicate` are unreachable.
//  · Each candidate's `itemId` is bound to its parent position item.
//  · `contactId` is `null` — the Zoho nominee pick is a later phase (P3b).
//  · A motion's threshold is DERIVED from its base ({@link thresholdForBase}).
//  · A motion carries its OPERATIVE WORDS and, where they are known, its two
//    attributions — trimmed, and `mover`/`seconder` sent as `null` when blank. An
//    empty string is a VALUE: the absence of a mover is not the mover being '', the
//    worker refuses one as `mover_invalid`, and migration 0016's trigger reads a
//    trim-blank as absent. This is the same `null`-for-nothing `motionClass` already
//    sends for "Not stated". The instrument needs no such guard — `validateDraft`
//    blocks an empty one before Create is offered.
//  · A POSITION carries NONE of the three (the worker refuses a position holding a
//    motion column as `position_has_motion_columns`).
//  · A POSITION spec omits `secrecy` (always 'secret'); a MOTION spec carries it.
//  · Titles and names are trimmed — the digest is over what is sent, and a stray
//    space is not a distinguishing part of a title.
// ═══════════════════════════════════════════════════════════════════════════

/** A typed-but-optional attribution as the wire wants it: the trimmed words where
 *  there are any, and `null` — absence — where there are none. The draft's unset
 *  form for a typed field is `''` (see {@link MotionItemDraft}), and `''` on the
 *  wire is a value rather than an absence: the worker refuses it (`mover_invalid` /
 *  `seconder_invalid`) and migration 0016's trigger reads a trim-blank as absent. */
function recordedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function buildSpec(draft: BallotDraft): CreateBallotSpec {
  const items: BallotItemInput[] = [];
  const candidates: BallotCandidateInput[] = [];

  draft.items.forEach((item, index) => {
    const agendaPosition = index + 1;
    const itemId = crypto.randomUUID();

    if (item.kind === 'position') {
      items.push({
        id: itemId,
        kind: 'position',
        agendaPosition,
        title: item.title.trim(),
        electorate: item.electorate,
        seats: item.seats,
        alternateSeats: item.alternateSeats,
        method: item.method,
      });
      for (const candidate of item.candidates) {
        candidates.push({
          id: crypto.randomUUID(),
          itemId,
          displayName: candidate.displayName.trim(),
          contactId: null,
        });
      }
    } else {
      const wire = item.majorityBase === null ? null : thresholdForBase(item.majorityBase);
      items.push({
        id: itemId,
        kind: 'motion',
        agendaPosition,
        title: item.title.trim(),
        // The operative words are REQUIRED, so no null arm: `validateDraft` refuses
        // an empty instrument and Create is not offered until it is filled.
        instrument: item.instrument.trim(),
        mover: recordedOrNull(item.mover),
        seconder: recordedOrNull(item.seconder),
        motionClass: item.motionClass,
        thresholdKind: wire === null ? null : wire.thresholdKind,
        thresholdNumerator: wire === null ? null : wire.thresholdNumerator,
        thresholdDenominator: wire === null ? null : wire.thresholdDenominator,
        majorityBase: item.majorityBase,
      });
    }
  });

  const base = {
    title: draft.title.trim(),
    itemKind: draft.kind,
    houseRule: draft.houseRule,
    items,
    candidates,
  };
  // A position ballot is always secret, so secrecy is OMITTED (not sent as a value
  // the server forces anyway); a motion carries the operator's choice.
  return draft.kind === 'motion' ? { ...base, secrecy: draft.secrecy } : base;
}
